import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { API_BASE } from '../api.config';
import { TopsIngestResult, TopsLocationsResponse } from '../models';

/**
 * Talks to the FastAPI backend for the formation-tops LOCATION workflow:
 *
 *   GET  /api/tops/locations   -> distinct Location values for the dropdown
 *   POST /api/tops/ingest       -> upload a TOPS file for a NEW location
 */
@Injectable({ providedIn: 'root' })
export class TopsService {
  private readonly http = inject(HttpClient);

  /** Distinct Location names known to the backend, for the dropdown. */
  readonly locations = signal<string[]>([]);

  /**
   * The Location currently selected for the tops read. An empty string means
   * "All locations" (no server-side Location filter), preserving the original
   * behaviour where tops are matched by well only.
   */
  readonly selectedLocation = signal<string>('');

  /** Load the distinct Location list and cache it in the signal. */
  loadLocations(): Observable<TopsLocationsResponse> {
    return this.http
      .get<TopsLocationsResponse>(`${API_BASE}/tops/locations`)
      .pipe(tap((res) => this.locations.set(res.locations ?? [])));
  }

  /** Record the Location the user picked (used when the GR job runs). */
  setSelectedLocation(location: string): void {
    this.selectedLocation.set(location ?? '');
  }

ingest(locationName: string, file: File, columnMap?: Record<string, string>): Observable<TopsIngestResult> {
  const form = new FormData();
  form.append('locationName', locationName);
  form.append('file', file, file.name);
  if (columnMap && Object.keys(columnMap).length) {
    form.append('columnMap', JSON.stringify(columnMap));
  }
  return this.http.post<TopsIngestResult>(`${API_BASE}/tops/ingest`, form).pipe(
    tap((res) => {
      const name = res.location;
      if (name && !this.locations().includes(name)) {
        this.locations.update((list) => [...list, name].sort());
      }
      this.setSelectedLocation(name);
    }),
  );
}
}
